import React, { useContext } from 'react';
import { ResponseError, SelectField, SubmitButton, AsyncSelectField } from '@nintendo/forms';
import { Button, Modal } from 'react-bootstrap';
import { FormikProps, Form } from 'formik';
import { NclDepartment } from '../../../api/amTemplateApiTypes';
import { getNclDepartments, getNclDevCodes } from '../../../api/amTemplateApi';
import { noOptionsHandler } from '../../../utils/displayUtils';
import ConfigContext from '../../../contexts/ConfigContext';
import useAsyncWithState from '../../../hooks/useAsyncWithState';

export interface UpdateContractServiceModalForm {
    amId: number;
    amNumber: number;
    supNumber: number;
    nclDepartmentCode: string;
    nclDepartmentDescription: string;
    nclDevCode: string;
    nclDevCodeDescription: string;
}

export interface ContractServiceUpdateFormProps
    extends FormikProps<UpdateContractServiceModalForm> {
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    toggleModal: () => void;
}

const ContractServiceUpdateForm = (props: ContractServiceUpdateFormProps) => {
    const config = useContext(ConfigContext);

    const { values, errors, touched, isSubmitting, dirty, responseError, toggleModal } = props;

    const { value: nclDepartments } = useAsyncWithState(
        () => getNclDepartments(config),
        [] as NclDepartment[],
    );

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>Edit NCL Codes</Modal.Title>
            </Modal.Header>
            <Form>
                <Modal.Body>
                    <ResponseError errorMessage={responseError} />

                    <SelectField
                        label="NCL Department"
                        name="nclDepartmentCode"
                        options={
                            nclDepartments
                                ? nclDepartments.map((nclDepartment) => ({
                                      id: nclDepartment.code,
                                      name: `[${nclDepartment.code}]${
                                          nclDepartment.description
                                              ? ` ${nclDepartment.description}`
                                              : ''
                                      }`,
                                  }))
                                : [
                                      {
                                          id: values.nclDepartmentCode,
                                          name: `[${values.nclDepartmentCode}]${
                                              values.nclDepartmentDescription
                                                  ? ` ${values.nclDepartmentDescription}`
                                                  : ''
                                          }`,
                                      },
                                  ]
                        }
                        touched={touched.nclDepartmentCode}
                        errors={errors.nclDepartmentCode}
                        testId="nclDepartmentCode"
                        isRequired
                    />
                    <AsyncSelectField
                        asyncSelectProps={{
                            cacheOptions: true,
                            loadOptions: async (inputValue: string) => {
                                if (inputValue.length > 2) {
                                    const nclDevCodes = await getNclDevCodes(config, inputValue);
                                    if (nclDevCodes) {
                                        return nclDevCodes.map((nclDevCode) => ({
                                            value: nclDevCode.code,
                                            label: ` [${nclDevCode.code}]${
                                                nclDevCode.description
                                                    ? ` ${nclDevCode.description}`
                                                    : ''
                                            }`,
                                        }));
                                    }
                                }
                                return [];
                            },
                            noOptionsMessage: noOptionsHandler,
                        }}
                        defaultValue={{
                            value: values.nclDevCode.toString(),
                            label: ` [${values.nclDevCode}]${
                                values.nclDevCodeDescription
                                    ? ` ${values.nclDevCodeDescription}`
                                    : ''
                            }`,
                        }}
                        label="NCL Dev Code"
                        name="nclDevCode"
                        touched={touched.nclDevCode}
                        errors={errors.nclDevCode}
                        testId="nclDevCode"
                        isRequired
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={toggleModal}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <SubmitButton
                        className="btn-primary ml-auto"
                        disabled={!dirty}
                        isSubmitting={isSubmitting}
                        buttonText="Save"
                    />
                </Modal.Footer>
            </Form>
        </>
    );
};

export default ContractServiceUpdateForm;
